import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Spinner,
  Form,
  InputGroup,
  Alert,
  Modal,
} from 'react-bootstrap';
import { 
  FaArrowLeft, 
  FaHeadset, 
  FaComment, 
  FaPaperPlane, 
  FaSync, 
  FaUser,
  FaUserShield,
  FaCalendar,
  FaClock,
  FaExclamationCircle,
  FaCommentDots,
  FaEnvelope,
  FaPhone,
  FaTicketAlt,
  FaEye,
  FaHistory,
  FaDownload,
  FaCopy,
  FaFileAlt,
  FaCalendarAlt,
  FaUserCircle
} from 'react-icons/fa';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const API_BASE = 'http://34.206.193.218:4001/api';

const pad2 = (value) => String(value).padStart(2, '0');

const formatTicketCode = (dateString) => {
  if (!dateString) return 'TKTUNKNOWN';

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return 'TKTUNKNOWN';

  return `TKT${date.getFullYear()}${pad2(date.getMonth() + 1)}${pad2(date.getDate())}${pad2(date.getHours())}${pad2(date.getMinutes())}`;
};

const HelpSupport = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ticketId, setTicketId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [messagePage, setMessagePage] = useState(1);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [ticketDetails, setTicketDetails] = useState(null);
  const [showTicketDetails, setShowTicketDetails] = useState(false);
  
  const token = localStorage.getItem('adminToken') || localStorage.getItem('token');

  // Fetch ticket ID for the project
  const fetchTicketId = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const res = await axios.get(
        `${API_BASE}/admin/data/getTicketIdByProject/${projectId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data.success && res.data.ticketId) {
        setTicketId(res.data.ticketId);
        fetchMessages(res.data.ticketId, 1);
      } else {
        setError('No support ticket found for this project');
        setLoading(false);
      }
    } catch (error) {
      console.error('Fetch ticket error:', error);
      setError('Error loading support data');
      setLoading(false);
      toast.error('Error loading support data');
    }
  };

  // Fetch messages for a ticket
  const fetchMessages = async (ticketId, page = 1) => {
    try {
      const res = await axios.get(
        `${API_BASE}/support/messages/${ticketId}?page=${page}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data.success) {
        const messagesData = res.data.messages || [];
        
        // Sort messages by date (oldest first)
        const sortedMessages = messagesData.sort((a, b) => 
          new Date(a.createdAt) - new Date(b.createdAt)
        );
        
        if (page === 1) {
          setMessages(sortedMessages);
        } else {
          setMessages(prev => [...prev, ...sortedMessages]);
        }
        setHasMoreMessages(res.data.hasMore || false);
        setMessagePage(page);
        
        // Extract ticket details from messages if available
        extractTicketDetails(messagesData, ticketId);
        
        // Scroll to bottom after messages load
        setTimeout(() => {
          scrollToBottom();
        }, 100);
      }
    } catch (error) {
      console.error('Fetch messages error:', error);
      toast.error('Error loading messages');
    } finally {
      setLoading(false);
    }
  };

  // Send a new message
  const sendMessage = async () => {
    if (!newMessage.trim() || !ticketId) return;
    
    try {
      setSending(true);
      
      const res = await axios.post(
        `${API_BASE}/support/adminSendMessage`,
        {
          ticketId,
          message: newMessage,
          senderRole: 'admin'
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data.success) {
        // Add new message to the end of the messages array
        const newMessageObj = {
          _id: res.data.message?._id || Date.now(),
          message: newMessage,
          senderRole: 'admin',
          createdAt: new Date().toISOString(),
          ticketId: ticketId
        };
        
        setMessages(prev => [...prev, newMessageObj]);
        setNewMessage('');
        toast.success('Message sent successfully');
        
        // Scroll to bottom to show new message
        setTimeout(() => {
          scrollToBottom();
        }, 100);
      }
    } catch (error) {
      console.error('Send message error:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Extract ticket details from messages or create fallback
  const extractTicketDetails = (messagesData, ticketId) => {
    if (messagesData.length > 0) {
      // Try to find the first message which might contain initial description
      const firstMessage = messagesData[0];
      const lastMessage = messagesData[messagesData.length - 1];
      
      // Check if messages have embedded ticket info
      if (firstMessage.ticketId) {
        // Use embedded ticket info if available
        setTicketDetails({
          _id: ticketId,
          status: firstMessage.ticketId.status || 'open',
          priority: firstMessage.ticketId.priority || 'medium',
          subject: firstMessage.ticketId.subject || `Support Ticket for Project ${projectId?.substring(0, 8)}...`,
          createdAt: firstMessage.ticketId.createdAt || firstMessage.createdAt,
          updatedAt: lastMessage.ticketId?.updatedAt || lastMessage.createdAt,
          description: firstMessage.ticketId.description || firstMessage.message
        });
      } else {
        // Create fallback ticket details from messages
        setTicketDetails({
          _id: ticketId,
          status: 'open',
          priority: 'medium',
          subject: `Support Ticket for Project ${projectId?.substring(0, 8)}...`,
          createdAt: firstMessage.createdAt,
          updatedAt: lastMessage.createdAt,
          description: firstMessage.message,
          // Estimate category based on message content
          category: estimateCategory(firstMessage.message)
        });
      }
    } else {
      // No messages yet, create basic ticket info
      setTicketDetails({
        _id: ticketId,
        status: 'open',
        priority: 'medium',
        subject: `Support Ticket for Project ${projectId?.substring(0, 8)}...`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        description: 'No messages yet',
        category: 'General'
      });
    }
  };

  // Estimate category based on message content
  const estimateCategory = (message) => {
    const msg = message.toLowerCase();
    if (msg.includes('error') || msg.includes('bug') || msg.includes('not working')) {
      return 'Technical Issue';
    } else if (msg.includes('question') || msg.includes('how to') || msg.includes('help')) {
      return 'Question';
    } else if (msg.includes('billing') || msg.includes('payment') || msg.includes('invoice')) {
      return 'Billing';
    } else if (msg.includes('feature') || msg.includes('request') || msg.includes('suggestion')) {
      return 'Feature Request';
    } else {
      return 'General Inquiry';
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInSeconds = Math.floor((now - date) / 1000);

      if (diffInSeconds < 60) return 'Just now';
      if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
      if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
      if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
      
      const months = Math.floor(diffInSeconds / 2592000);
      if (months < 1) return `${Math.floor(diffInSeconds / 86400)} days ago`;
      return `${months} month${months !== 1 ? 's' : ''} ago`;
    } catch {
      return '';
    }
  };

  const loadMoreMessages = () => {
    if (ticketId && hasMoreMessages) {
      fetchMessages(ticketId, messagePage + 1);
    }
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'open':
        return <Badge bg="success">Open</Badge>;
      case 'closed':
        return <Badge bg="secondary">Closed</Badge>;
      case 'pending':
        return <Badge bg="warning">Pending</Badge>;
      case 'resolved':
        return <Badge bg="info">Resolved</Badge>;
      case 'escalated':
        return <Badge bg="danger">Escalated</Badge>;
      default:
        return <Badge bg="light" text="dark">Unknown</Badge>;
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'low':
        return <Badge bg="secondary">Low</Badge>;
      case 'medium':
        return <Badge bg="primary">Medium</Badge>;
      case 'high':
        return <Badge bg="warning">High</Badge>;
      case 'urgent':
        return <Badge bg="danger">Urgent</Badge>;
      default:
        return <Badge bg="light" text="dark">Standard</Badge>;
    }
  };

  const getCategoryBadge = (category) => {
    switch (category?.toLowerCase()) {
      case 'technical issue':
        return <Badge bg="danger">Technical Issue</Badge>;
      case 'question':
        return <Badge bg="info">Question</Badge>;
      case 'billing':
        return <Badge bg="warning">Billing</Badge>;
      case 'feature request':
        return <Badge bg="success">Feature Request</Badge>;
      case 'general inquiry':
      default:
        return <Badge bg="secondary">General Inquiry</Badge>;
    }
  };

  const exportConversation = () => {
    if (messages.length === 0) {
      toast.info('No messages to export');
      return;
    }

    const conversation = messages.map((msg, index) => {
      const sender = msg.senderRole === 'admin' ? 'ADMIN' : 'USER';
      const time = formatDate(msg.createdAt);
      return `[${index + 1}] [${time}] [${sender}]: ${msg.message}`;
    }).join('\n\n');

    const ticketInfo = `
Support Ticket Summary
======================
Ticket ID: ${ticketId}
Ticket Code: ${ticketDisplayCode}
Project ID: ${projectId}
Status: ${ticketDetails?.status || 'Unknown'}
Priority: ${ticketDetails?.priority || 'Unknown'}
Category: ${ticketDetails?.category || 'Unknown'}
Created: ${formatDate(ticketDetails?.createdAt)}
Last Updated: ${formatDate(ticketDetails?.updatedAt)}
Total Messages: ${messages.length}

Conversation History
====================
${conversation}

Generated on: ${new Date().toLocaleString()}
    `;

    const blob = new Blob([ticketInfo], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ticket-${ticketId.substring(0, 8)}-conversation.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    toast.success('Conversation exported successfully');
  };

  const copyTicketId = () => {
    if (ticketDisplayCode) {
      navigator.clipboard.writeText(ticketDisplayCode);
      toast.success('Ticket code copied to clipboard');
    }
  };

  const copyProjectId = () => {
    if (projectId) {
      navigator.clipboard.writeText(projectId);
      toast.success('Project ID copied to clipboard');
    }
  };

  // Calculate conversation statistics
  const calculateStats = () => {
    const adminMessages = messages.filter(m => m.senderRole === 'admin').length;
    const userMessages = messages.filter(m => m.senderRole === 'user').length;
    
    let earliestDate = null;
    let latestDate = null;
    
    if (messages.length > 0) {
      earliestDate = new Date(Math.min(...messages.map(m => new Date(m.createdAt).getTime())));
      latestDate = new Date(Math.max(...messages.map(m => new Date(m.createdAt).getTime())));
    }
    
    return {
      total: messages.length,
      admin: adminMessages,
      user: userMessages,
      earliest: earliestDate,
      latest: latestDate,
      duration: earliestDate && latestDate 
        ? Math.ceil((latestDate - earliestDate) / (1000 * 60 * 60 * 24)) 
        : 0
    };
  };

  const ticketDisplayCode = formatTicketCode(
    ticketDetails?.createdAt || messages[0]?.createdAt
  );

  useEffect(() => {
    if (projectId) {
      fetchTicketId();
    }
  }, [projectId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Loading support data...</span>
      </div>
    );
  }

  const stats = calculateStats();

  return (
    <Container fluid className="py-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* HEADER */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start mb-4">
            <div>
              <Button
                variant="outline-primary"
                onClick={() => navigate(-1)}
                className="mb-3 d-flex align-items-center"
              >
                <FaArrowLeft className="me-2" />
                Back to Projects List
              </Button>
              <h2 className="fw-bold mb-2">
                <FaHeadset className="me-3" />
                Help & Support
              </h2>
              <div className="d-flex align-items-center flex-wrap gap-2">
                <Badge bg="primary" className="px-3 py-2">
                  <FaFileAlt className="me-2" />
                  Project ID: {projectId?.substring(0, 8)}...
                  <Button 
                    variant="link" 
                    className="text-white p-0 ms-2"
                    onClick={copyProjectId}
                    size="sm"
                  >
                    <FaCopy size={12} />
                  </Button>
                </Badge>
                {ticketId && (
                  <Badge bg="success" className="px-3 py-2">
                    <FaTicketAlt className="me-2" />
                    Ticket: {ticketDisplayCode}
                    <Button 
                      variant="link" 
                      className="text-white p-0 ms-2"
                      onClick={copyTicketId}
                      size="sm"
                    >
                      <FaCopy size={12} />
                    </Button>
                  </Badge>
                )}
                <Badge bg="info" className="px-3 py-2">
                  <FaCommentDots className="me-2" />
                  {messages.length} Message{messages.length !== 1 ? 's' : ''}
                </Badge>
              </div>
            </div>
            
            <div className="d-flex gap-2">
              {ticketId && (
                <>
                  <Button
                    variant="outline-primary"
                    onClick={() => fetchMessages(ticketId, 1)}
                    className="d-flex align-items-center"
                  >
                    <FaSync className="me-2" />
                    Refresh
                  </Button>
                  <Button
                    variant="outline-secondary"
                    onClick={exportConversation}
                    className="d-flex align-items-center"
                  >
                    <FaDownload className="me-2" />
                    Export
                  </Button>
                  <Button
                    variant="outline-info"
                    onClick={() => setShowTicketDetails(true)}
                    className="d-flex align-items-center"
                  >
                    <FaEye className="me-2" />
                    Details
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* TICKET INFO SUMMARY */}
          {ticketId && ticketDetails && (
            <Card className="mb-4 border-0 shadow-sm bg-light">
              <Card.Body>
                <Row className="g-3">
                  <Col md={3}>
                    <div className="d-flex align-items-center">
                      <div className="bg-primary bg-opacity-10 rounded-circle p-3 me-3">
                        <FaTicketAlt className="text-primary" size={20} />
                      </div>
                      <div>
                        <small className="text-muted">Status</small>
                        <div>{getStatusBadge(ticketDetails.status)}</div>
                      </div>
                    </div>
                  </Col>
                  
                  <Col md={3}>
                    <div className="d-flex align-items-center">
                      <div className="bg-warning bg-opacity-10 rounded-circle p-3 me-3">
                        <FaExclamationCircle className="text-warning" size={20} />
                      </div>
                      <div>
                        <small className="text-muted">Priority</small>
                        <div>{getPriorityBadge(ticketDetails.priority)}</div>
                      </div>
                    </div>
                  </Col>
                  
                  <Col md={3}>
                    <div className="d-flex align-items-center">
                      <div className="bg-info bg-opacity-10 rounded-circle p-3 me-3">
                        <FaFileAlt className="text-info" size={20} />
                      </div>
                      <div>
                        <small className="text-muted">Category</small>
                        <div>{getCategoryBadge(ticketDetails.category)}</div>
                      </div>
                    </div>
                  </Col>
                  
                  <Col md={3}>
                    <div className="d-flex align-items-center">
                      <div className="bg-success bg-opacity-10 rounded-circle p-3 me-3">
                        <FaHistory className="text-success" size={20} />
                      </div>
                      <div>
                        <small className="text-muted">Last Updated</small>
                        <div className="fw-medium">{formatDate(ticketDetails.updatedAt)}</div>
                      </div>
                    </div>
                  </Col>
                </Row>

                <div className="mt-3">
                  <small className="text-muted">Ticket Code:</small>
                  <h6 className="mb-0">{ticketDisplayCode}</h6>
                </div>

                {ticketDetails.subject && (
                  <div className="mt-3">
                    <small className="text-muted">Subject:</small>
                    <h6 className="mb-0">{ticketDetails.subject}</h6>
                  </div>
                )}
              </Card.Body>
            </Card>
          )}


          {/* MAIN CONTENT */}
          {!ticketId ? (
            <Card className="border-0 shadow-sm">
              <Card.Body className="text-center py-5">
                <FaHeadset size={64} className="text-muted mb-3" />
                <h4 className="mb-3">No Support Ticket Found</h4>
                <p className="text-muted mb-4">
                  This project doesn't have an active support ticket yet.
                  Support tickets are created through the main application interface.
                </p>
                <div className="d-flex justify-content-center gap-3">
                  <Button 
                    variant="outline-primary" 
                    onClick={() => navigate(-1)}
                  >
                    <FaArrowLeft className="me-2" />
                    Go Back
                  </Button>
                  <Button 
                    variant="outline-secondary"
                    onClick={fetchTicketId}
                  >
                    <FaSync className="me-2" />
                    Check Again
                  </Button>
                </div>
              </Card.Body>
            </Card>
          ) : (
            <>
              {/* MESSAGES CONTAINER */}
              <Card className="border-0 shadow-sm mb-4" style={{ height: '500px' }}>
                <Card.Header className="bg-primary text-white border-0 d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-0">
                      <FaComment className="me-2" />
                      Support Conversation
                    </h5>
                    <small>
                      Ticket: {ticketDisplayCode}
                      <Button 
                        variant="link" 
                        className="text-white p-0 ms-2"
                        onClick={copyTicketId}
                        size="sm"
                      >
                        <FaCopy size={12} />
                      </Button>
                    </small>
                  </div>
                  <div className="d-flex gap-2">
                    <Badge bg="light" text="dark">
                      {messages.length} messages
                    </Badge>
                    <Button
                      variant="light"
                      size="sm"
                      onClick={exportConversation}
                      title="Export Conversation"
                    >
                      <FaDownload size={14} />
                    </Button>
                  </div>
                </Card.Header>
                
                <Card.Body className="p-0" style={{ overflowY: 'auto' }}>
                  <div className="p-3">
                    {hasMoreMessages && messages.length > 10 && (
                      <div className="text-center mb-3">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={loadMoreMessages}
                        >
                          Load Older Messages
                        </Button>
                      </div>
                    )}
                    
                    {messages.length === 0 ? (
                      <div className="text-center py-5">
                        <FaComment size={48} className="text-muted mb-3" />
                        <h5>No Messages Yet</h5>
                        <p className="text-muted">
                          Start the conversation by sending a message
                        </p>
                      </div>
                    ) : (
                      <div className="chat-messages">
                        {messages.map((message, index) => (
                          <div 
                            key={message._id || index} 
                            className={`mb-3 ${message.senderRole === 'admin' ? 'text-end' : ''}`}
                          >
                            <div 
                              className={`d-inline-block p-3 rounded-3 ${message.senderRole === 'admin' ? 'bg-primary text-white' : 'bg-light'}`}
                              style={{ maxWidth: '70%' }}
                            >
                              <div className="mb-1">{message.message}</div>
                              <div className="small d-flex align-items-center">
                                {message.senderRole === 'admin' ? (
                                  <>
                                    <FaUserShield className="me-1" />
                                    <span className="me-2">Admin</span>
                                  </>
                                ) : (
                                  <>
                                    <FaUser className="me-1" />
                                    <span className="me-2">User</span>
                                  </>
                                )}
                                <FaClock className="me-1" />
                                <span>{getTimeAgo(message.createdAt)}</span>
                              </div>
                            </div>
                            <div className="small text-muted mt-1">
                              {formatDate(message.createdAt)}
                            </div>
                          </div>
                        ))}
                        <div ref={messagesEndRef} />
                      </div>
                    )}
                  </div>
                </Card.Body>
                
                {/* MESSAGE INPUT */}
                <Card.Footer className="border-0 bg-light">
                  <InputGroup>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="Type your message here..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          sendMessage();
                        }
                      }}
                      disabled={sending}
                    />
                    <Button 
                      variant="primary" 
                      onClick={sendMessage}
                      disabled={!newMessage.trim() || sending}
                    >
                      {sending ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <>
                          <FaPaperPlane className="me-2" />
                          Send
                        </>
                      )}
                    </Button>
                  </InputGroup>
                  <div className="small text-muted mt-2">
                    Press Enter to send, Shift+Enter for new line
                  </div>
                </Card.Footer>
              </Card>

              {/* CONVERSATION STATS */}
              <Row className="mb-4">
                <Col md={3}>
                  <Card className="border-0 shadow-sm text-center h-100">
                    <Card.Body>
                      <h2 className="text-primary">{stats.total}</h2>
                      <h6>Total Messages</h6>
                      <small className="text-muted">
                        In this conversation
                      </small>
                    </Card.Body>
                  </Card>
                </Col>
                
                <Col md={3}>
                  <Card className="border-0 shadow-sm text-center h-100">
                    <Card.Body>
                      <h2 className="text-success">{stats.admin}</h2>
                      <h6>Admin Messages</h6>
                      <small className="text-muted">
                        Support team responses
                      </small>
                    </Card.Body>
                  </Card>
                </Col>
                
                <Col md={3}>
                  <Card className="border-0 shadow-sm text-center h-100">
                    <Card.Body>
                      <h2 className="text-info">{stats.user}</h2>
                      <h6>User Messages</h6>
                      <small className="text-muted">
                        Customer inquiries
                      </small>
                    </Card.Body>
                  </Card>
                </Col>
                
                <Col md={3}>
                  <Card className="border-0 shadow-sm text-center h-100">
                    <Card.Body>
                      <h6 className="text-muted mb-2">Duration</h6>
                      {stats.duration > 0 ? (
                        <>
                          <h5 className="mb-1">{stats.duration} day{stats.duration !== 1 ? 's' : ''}</h5>
                          <small className="text-muted">
                            Active support period
                          </small>
                        </>
                      ) : (
                        <p className="text-muted mb-0">Single message</p>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          )}

          {/* ERROR STATE */}
          {error && !ticketId && (
            <Alert variant="warning" className="mt-4">
              <div className="d-flex align-items-center">
                <FaExclamationCircle className="me-3" size={24} />
                <div>
                  <h6 className="mb-1">No Active Support Ticket</h6>
                  <p className="mb-0">{error}</p>
                  <div className="d-flex gap-2 mt-2">
                    <Button 
                      variant="outline-warning" 
                      size="sm"
                      onClick={fetchTicketId}
                    >
                      <FaSync className="me-2" />
                      Retry
                    </Button>
                    <Button 
                      variant="outline-secondary" 
                      size="sm"
                      onClick={() => navigate(-1)}
                    >
                      <FaArrowLeft className="me-2" />
                      Go Back
                    </Button>
                  </div>
                </div>
              </div>
            </Alert>
          )}
        </Card.Body>
      </Card>

      {/* TICKET DETAILS MODAL */}
      <Modal
        show={showTicketDetails}
        onHide={() => setShowTicketDetails(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaTicketAlt className="me-2" />
            Ticket Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {ticketDetails ? (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <div className="mb-3">
                    <small className="text-muted">Ticket ID</small>
                    <div className="d-flex align-items-center">
                      <code className="text-truncate">{ticketId}</code>
                      <Button 
                        variant="link" 
                        size="sm"
                        onClick={copyTicketId}
                        className="ms-2"
                      >
                        <FaCopy />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <small className="text-muted">Status</small>
                    <div>{getStatusBadge(ticketDetails.status)}</div>
                  </div>
                  
                  <div className="mb-3">
                    <small className="text-muted">Priority</small>
                    <div>{getPriorityBadge(ticketDetails.priority)}</div>
                  </div>
                  
                  <div className="mb-3">
                    <small className="text-muted">Category</small>
                    <div>{getCategoryBadge(ticketDetails.category)}</div>
                  </div>
                </Col>
                
                <Col md={6}>
                  <div className="mb-3">
                    <small className="text-muted">Created</small>
                    <div className="fw-medium d-flex align-items-center">
                      <FaCalendarAlt className="me-2" />
                      {formatDate(ticketDetails.createdAt)}
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <small className="text-muted">Last Updated</small>
                    <div className="fw-medium d-flex align-items-center">
                      <FaHistory className="me-2" />
                      {formatDate(ticketDetails.updatedAt)}
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <small className="text-muted">Project ID</small>
                    <div className="d-flex align-items-center">
                      {projectId}
                      <Button 
                        variant="link" 
                        size="sm"
                        onClick={copyProjectId}
                        className="ms-2"
                      >
                        <FaCopy />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <small className="text-muted">Message Count</small>
                    <div className="fw-medium">{messages.length}</div>
                  </div>
                </Col>
              </Row>
              
              <hr />
              
              <div className="mb-3">
                <small className="text-muted">Subject</small>
                <h6>{ticketDetails.subject || 'No subject provided'}</h6>
              </div>
              
              {ticketDetails.description && (
                <div className="mb-3">
                  <small className="text-muted">Initial Description</small>
                  <div className="p-3 bg-light rounded">
                    <p className="mb-0">{ticketDetails.description}</p>
                  </div>
                </div>
              )}
              
              {/* Sender info if available */}
              {messages.length > 0 && messages[0].senderId && (
                <div className="mb-3">
                  <small className="text-muted">Created By</small>
                  <div className="d-flex align-items-center mt-1">
                    <FaUserCircle className="me-2 text-secondary" />
                    <span>User ID: {messages[0].senderId.substring(0, 8)}...</span>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading ticket details...</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowTicketDetails(false)}
          >
            Close
          </Button>
          <Button 
            variant="primary"
            onClick={exportConversation}
          >
            <FaDownload className="me-2" />
            Export Conversation
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default HelpSupport;
